import { resolveSteps, tourAllowed } from '../resolve-steps';
import { TOURS, tourForPath } from '../tours/registry';
import type { StepContext, TourFeature, TourKey } from '../tours/types';
import type { StruggleTrip } from './score';

/**
 * What to offer someone who seems stuck (critic C6), most specific first:
 *
 *  1. an inline form's own tour, at the step the person was in (a form abandon);
 *  2. the page's tour AT the step whose anchor the signal came from;
 *  3. the page's tour from the start;
 *  4. otherwise the page's help notes / support (non-recording).
 *
 * A page tour is offered only while its chip is on screen and ready (the
 * offer renders beside the chip); a tour the viewer may not see, or whose
 * steps they would not get, is skipped.
 */
export type HelpTarget =
  { kind: 'tour'; tour: TourKey; startAtStepId?: string } | { kind: 'notes'; section: string };

export interface HelpResolveInput {
  trip: StruggleTrip;
  pathname: string;
  search: string;
  ctx: StepContext;
  features: Readonly<Record<TourFeature, boolean>>;
  /** Which page chips are mounted and ready right now. */
  pageReady: Partial<Record<TourKey, boolean>>;
}

function stepForAnchor(key: TourKey, ctx: StepContext, anchor: string | null): string | undefined {
  if (!anchor) return undefined;
  return resolveSteps(TOURS[key], ctx, { trim: false }).find((step) => step.anchor === anchor)?.id;
}

export function resolveHelpTarget(input: HelpResolveInput): HelpTarget {
  const { trip, ctx, features } = input;
  const usable = (key: TourKey): boolean => {
    const tour = TOURS[key];
    return (
      tourAllowed(tour, ctx.permissions, features) &&
      resolveSteps(tour, ctx, { trim: false }).length >= tour.minSteps
    );
  };

  if (trip.host) {
    if (usable(trip.host)) {
      const startAtStepId = stepForAnchor(trip.host, ctx, trip.anchor);
      return { kind: 'tour', tour: trip.host, ...(startAtStepId ? { startAtStepId } : {}) };
    }
    return { kind: 'notes', section: trip.section };
  }

  const page = tourForPath(input.pathname, input.search);
  if (page && usable(page.key) && input.pageReady[page.key]) {
    const startAtStepId = stepForAnchor(page.key, ctx, trip.anchor);
    return { kind: 'tour', tour: page.key, ...(startAtStepId ? { startAtStepId } : {}) };
  }
  return { kind: 'notes', section: trip.section };
}
